import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";
import type { HistoryEntry } from "../hooks/usePawMatchHistory.js";
import {
  pawmatchStorage,
  type PawMatchSession,
} from "../lib/appStorage.js";

/** 30 minutes in milliseconds */
const CACHE_TTL_MS = 30 * 60 * 1000;

interface PawMatchSessionContextType {
  // ─── Feed cache ────────────────────────────────────────────
  feedAnimals: any[];
  cursor: string | null;
  feedExhausted: boolean;
  currentIndex: number;
  lastFetchedAt: number | null;
  /** Preferences fingerprint this cache was built for (species + filters). */
  feedPrefsKey: string | null;
  /** Client-side set of all animal IDs swiped in this session (both directions).
   *  Used to filter the swipeable feed immediately, before the DB write commits. */
  swipedAnimalIds: Set<string>;
  addSwipedId: (id: string) => void;

  /** @deprecated Prefer isCacheValidForPrefs — TTL-only check ignores filter changes. */
  isCacheValid: () => boolean;
  /** True when TTL is valid, cache non-empty, and prefs match the current key. */
  isCacheValidForPrefs: (prefsKey: string) => boolean;
  /** Replace the entire feed cache (used after a fresh first-page fetch). */
  updateFeedCache: (
    animals: any[],
    cursor: string | null,
    feedExhausted: boolean,
    prefsKey: string,
  ) => void;
  /** Append animals from fetchNextPage (pagination). */
  appendToFeed: (
    animals: any[],
    cursor: string | null,
    feedExhausted: boolean,
  ) => void;
  setCurrentIndex: (index: number) => void;
  clearFeedCache: () => void;

  // ─── History ───────────────────────────────────────────────
  history: HistoryEntry[];
  historyLoaded: boolean;
  setHistory: (entries: HistoryEntry[]) => void;
  /** Optimistically prepend a new entry. */
  appendHistoryEntry: (entry: HistoryEntry) => void;
  /** Roll back an optimistic entry on API failure (matched by clientTimestamp + animalId). */
  removeHistoryEntry: (clientTimestamp: number, animalId: string) => void;
  /** Mark an optimistic entry as confirmed by setting its server id and clearing pending. */
  confirmHistoryEntry: (
    clientTimestamp: number,
    animalId: string,
    serverId: string,
  ) => void;
}

const PawMatchSessionContext = createContext<
  PawMatchSessionContextType | undefined
>(undefined);

export function usePawMatchSession() {
  const ctx = useContext(PawMatchSessionContext);
  if (!ctx) {
    throw new Error(
      "usePawMatchSession must be used within a PawMatchSessionProvider",
    );
  }
  return ctx;
}

interface Props {
  children: React.ReactNode;
}

export const PawMatchSessionProvider: React.FC<Props> = ({ children }) => {
  // ── Feed state ──────────────────────────────────────────────
  const [feedAnimals, setFeedAnimals] = useState<any[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [feedExhausted, setFeedExhausted] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [feedPrefsKey, setFeedPrefsKey] = useState<string | null>(null);
  const lastFetchedAtRef = useRef<number | null>(null);
  // Tracks the prefs key from the last updateFeedCache call so we know
  // whether the index should be preserved or reset.
  const lastUpdatePrefsKeyRef = useRef<string | null>(null);
  const [swipedAnimalIds, setSwipedAnimalIds] = useState<Set<string>>(
    new Set(),
  );

  // ── History state ───────────────────────────────────────────
  const [history, setHistoryState] = useState<HistoryEntry[]>([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  // ── Persist feed slice to appStorage (iOS) / sessionStorage (web) ──────────
  // Load persisted session on first mount.
  useEffect(() => {
    let cancelled = false;
    pawmatchStorage.loadSession().then((saved) => {
      if (cancelled || !saved) return;
      setFeedPrefsKey(saved.feedPrefsKey);
      setCurrentIndex(saved.currentIndex);
      setCursor(saved.cursor);
      setSwipedAnimalIds(new Set(saved.swipedAnimalIds));
      lastUpdatePrefsKeyRef.current = saved.feedPrefsKey;
      // lastFetchedAt is not restored — the feed animals themselves come from
      // the API; the session only preserves position and swiped-ID filter.
    });
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Feed helpers ────────────────────────────────────────────
  const isCacheValid = useCallback(() => {
    if (lastFetchedAtRef.current === null) return false;
    if (feedAnimals.length === 0) return false;
    return Date.now() - lastFetchedAtRef.current < CACHE_TTL_MS;
  }, [feedAnimals.length]);

  const isCacheValidForPrefs = useCallback(
    (prefsKey: string) => {
      if (!prefsKey) return false;
      if (lastFetchedAtRef.current === null) return false;
      if (feedAnimals.length === 0) return false;
      if (feedPrefsKey == null || feedPrefsKey !== prefsKey) return false;
      return Date.now() - lastFetchedAtRef.current < CACHE_TTL_MS;
    },
    [feedAnimals.length, feedPrefsKey],
  );

  const updateFeedCache = useCallback(
    (
      animals: any[],
      newCursor: string | null,
      exhausted: boolean,
      prefsKey: string,
    ) => {
      setFeedAnimals(animals);
      setCursor(newCursor);
      setFeedExhausted(exhausted);
      // Only reset the index when the preferences actually changed.
      // This preserves the swipe position when returning to PawMatch
      // within the same session (same prefs, cache still valid).
      if (lastUpdatePrefsKeyRef.current !== prefsKey) {
        setCurrentIndex(0);
      }
      lastUpdatePrefsKeyRef.current = prefsKey;
      setFeedPrefsKey(prefsKey);
      lastFetchedAtRef.current = Date.now();
    },
    [],
  );

  const appendToFeed = useCallback(
    (animals: any[], newCursor: string | null, exhausted: boolean) => {
      setFeedAnimals((prev) => [...prev, ...animals]);
      setCursor(newCursor);
      setFeedExhausted(exhausted);
    },
    [],
  );

  const addSwipedId = useCallback(
    (id: string) => {
      setSwipedAnimalIds((prev) => {
        const next = new Set(prev).add(id);
        // Persist updated session on every swipe.
        const session: PawMatchSession = {
          currentIndex,
          cursor,
          swipedAnimalIds: Array.from(next),
          feedPrefsKey,
          savedAt: Date.now(),
        };
        pawmatchStorage.saveSession(session);
        return next;
      });
    },
    [currentIndex, cursor, feedPrefsKey],
  );

  const clearFeedCache = useCallback(() => {
    setFeedAnimals([]);
    setCursor(null);
    setFeedExhausted(false);
    setCurrentIndex(0);
    setFeedPrefsKey(null);
    setSwipedAnimalIds(new Set());
    lastFetchedAtRef.current = null;
    lastUpdatePrefsKeyRef.current = null;
    pawmatchStorage.clearSession();
  }, []);

  // ── History helpers ─────────────────────────────────────────
  const setHistory = useCallback((entries: HistoryEntry[]) => {
    setHistoryState(entries);
    setHistoryLoaded(true);
  }, []);

  const appendHistoryEntry = useCallback((entry: HistoryEntry) => {
    setHistoryState((prev) => [entry, ...prev].slice(0, 10));
  }, []);

  const removeHistoryEntry = useCallback(
    (clientTimestamp: number, animalId: string) => {
      setHistoryState((prev) =>
        prev.filter(
          (e) =>
            !(e.clientTimestamp === clientTimestamp && e.animalId === animalId),
        ),
      );
    },
    [],
  );

  const confirmHistoryEntry = useCallback(
    (clientTimestamp: number, animalId: string, serverId: string) => {
      setHistoryState((prev) =>
        prev.map((e) =>
          e.clientTimestamp === clientTimestamp && e.animalId === animalId
            ? { ...e, id: serverId, pending: false, clientTimestamp: undefined }
            : e,
        ),
      );
    },
    [],
  );

  const value: PawMatchSessionContextType = {
    feedAnimals,
    cursor,
    feedExhausted,
    currentIndex,
    lastFetchedAt: lastFetchedAtRef.current,
    feedPrefsKey,
    swipedAnimalIds,
    addSwipedId,
    isCacheValid,
    isCacheValidForPrefs,
    updateFeedCache,
    appendToFeed,
    setCurrentIndex,
    clearFeedCache,
    history,
    historyLoaded,
    setHistory,
    appendHistoryEntry,
    removeHistoryEntry,
    confirmHistoryEntry,
  };

  return (
    <PawMatchSessionContext.Provider value={value}>
      {children}
    </PawMatchSessionContext.Provider>
  );
};
